import React, { useRef, useMemo, useEffect } from 'react';
import { useThree } from '@react-three/fiber';
import * as THREE from 'three';

const BOARD_ROWS = 20;
const BOARD_COLUMNS = 10;
const MAX_BLOCKS = BOARD_ROWS * BOARD_COLUMNS; // 200 blocks max
const BLOCK_SIZE = 0.88;
const BLOCK_DEPTH = 0.3; // Flatter depth for default theme

// Shared geometry - flat blocks
const blockGeometry = new THREE.BoxGeometry(BLOCK_SIZE, BLOCK_SIZE, BLOCK_DEPTH);

// Temp objects for matrix calculations (reused to avoid GC)
const tempMatrix = new THREE.Matrix4();
const tempPosition = new THREE.Vector3();
const tempQuaternion = new THREE.Quaternion();
const tempScale = new THREE.Vector3(1, 1, 1);
const zeroScale = new THREE.Vector3(0, 0, 0);

// Single color for all blocks - simple purple, flat matte material
const SINGLE_BLOCK_COLOR = '#9B59B6';

const createMaterial = () => new THREE.MeshStandardMaterial({
  color: new THREE.Color(SINGLE_BLOCK_COLOR),
  emissive: new THREE.Color('#000000'),
  emissiveIntensity: 0,
  metalness: 0,
  roughness: 1,
  side: THREE.DoubleSide,
});

/**
 * DefaultInstancedBoard - Simple flat rendering for default theme
 * No glow effects, single color, flat appearance
 */
const DefaultInstancedBoard = ({
  boardMatrix,
  boardVersion = 0,
}) => {
  const instanceRef = useRef();
  const blockCountRef = useRef(0);

  // Single material for all blocks
  const material = useMemo(() => createMaterial(), []);

  // Get invalidate for demand-mode rendering
  const { invalidate } = useThree();

  // Helper to initialize mesh by hiding all instances
  const initializeMesh = (mesh) => {
    if (!mesh) return;
    tempMatrix.compose(tempPosition, tempQuaternion, zeroScale);
    for (let i = 0; i < MAX_BLOCKS; i++) {
      mesh.setMatrixAt(i, tempMatrix);
    }
    mesh.instanceMatrix.needsUpdate = true;
  };

  // Ref callback that initializes mesh when first created
  const setMeshRef = (el) => {
    if (el && !instanceRef.current) {
      instanceRef.current = el;
      initializeMesh(el);
    } else if (el) {
      instanceRef.current = el;
    }
  };

  // Update instances when board changes
  useEffect(() => {
    if (!boardMatrix || !instanceRef.current) return;

    const mesh = instanceRef.current;
    const blocks = [];

    // Parse board matrix - collect all filled cells
    for (let row = 0; row < boardMatrix.length; row++) {
      const rowData = boardMatrix[row];
      if (!rowData) continue;

      const colLimit = Math.min(rowData.length, BOARD_COLUMNS);
      for (let col = 0; col < colLimit; col++) {
        const cell = rowData[col];
        // Check if cell is filled (any truthy value that's not 0 or empty string)
        if (cell && cell !== 0 && cell !== '') {
          blocks.push({ row, col });
        }
      }
    }

    blockCountRef.current = blocks.length;

    // Update each block instance
    blocks.forEach((block, index) => {
      const x = block.col - (BOARD_COLUMNS / 2) + 0.5;
      const y = (BOARD_ROWS - block.row) - 0.5;

      tempPosition.set(x, y, 0);
      tempMatrix.compose(tempPosition, tempQuaternion, tempScale);
      mesh.setMatrixAt(index, tempMatrix);
    });

    // Hide unused instances by scaling to 0
    for (let i = blocks.length; i < MAX_BLOCKS; i++) {
      tempMatrix.compose(tempPosition, tempQuaternion, zeroScale);
      mesh.setMatrixAt(i, tempMatrix);
    }

    // Mark matrix as needing update
    mesh.instanceMatrix.needsUpdate = true;

    // Force frame update for demand-mode rendering
    invalidate();
  }, [boardMatrix, boardVersion, invalidate]);

  return (
    <instancedMesh
      ref={setMeshRef}
      args={[blockGeometry, material, MAX_BLOCKS]}
      castShadow
      receiveShadow
      frustumCulled={false}
    />
  );
};

export default DefaultInstancedBoard;
